import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/useAuthStore';
import { AUTO_LOGOUT_CODES } from './types/auth';
import { toast } from './lib/use-toast';

// Global fetch interceptor — handles session revocation for all fetch() calls.
// Fires once per page lifetime to avoid duplicate redirects from parallel requests.
let _sessionExpiredHandled = false;
const _handleSessionExpired = (description: string) => {
  if (_sessionExpiredHandled) return;
  _sessionExpiredHandled = true;
  useAuthStore.getState().logout();
  toast({
    variant: 'destructive',
    title: 'Session Expired',
    description,
  });
  window.location.href = '/';
};

const _originalFetch = window.fetch;
window.fetch = async (input, init) => {
  const response = await _originalFetch(input, init);
  if (!response.ok) {
    // Treat any 401/403 as a session issue — covers Spring's default
    // "Access Denied" 403 envelope which has no errors[0].code.
    const isAuthStatus = response.status === 401 || response.status === 403;
    let description = 'Your session has expired. Please login again.';
    try {
      const errorData = await response.clone().json();
      const errorCode = errorData?.errors?.[0]?.code;
      const matchedCode = errorCode && AUTO_LOGOUT_CODES.includes(errorCode);
      if (matchedCode || isAuthStatus) {
        description = errorData?.errors?.[0]?.message || errorData?.message || description;
        _handleSessionExpired(description);
      }
    } catch (_) {
      // Body wasn't JSON — still trip auto-logout on 401/403.
      if (isAuthStatus) _handleSessionExpired(description);
    }
  }
  return response;
};

const queryClient = new QueryClient();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
